import {gsap, TimelineLite} from "gsap";
import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {WalkOfShameSymbol} from "../symbol/WalkOfShameSymbol";
import {WalkOfShameReelView} from "./WalkOfShameReelView";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {MathHelper} from "../../core/utils/MathHelper";
import {WalkOfShame} from "../WalkOfShame";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {WalkOfShameGameConfig} from "../WalkOfShameGameConfig";
import {WalkOfShameSymbolConfig, WalkOfShameSymbolState} from "../symbol/WalkOfShameSymbolConfig";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class WalkOfShameReelController {
    public readonly events: APIEventSystem;

    public reelId: number;
    public reelView: WalkOfShameReelView;
    public isQuickStop: boolean;

    private readonly _symbolsNumInData: number[] = [0, 1, 4, 1, 0]; // see legacy doc wiki about reel config.
    private readonly _symsNumTotal: number;
    private _currentReelSet: string[]; //The reelSet currently in use, it might be different from the actually reelSet because it's made of many parts.
    private _currentFullReelSet: string[]; // The full actually reelSet that the most part of the _currentReelSet is make of.
    private _stopReelSetFilledBottomNum: number = 0; // Use to calculate added symbols under the server reel symbols when stopping.
    private _extraStopDelay: number;
    private _reelStopDelayTimer: TweenLite | null; //Delay for reel stop
    private _stopCounter: number = 0;

    private _normalStartStepDuration: number = 0.3;
    private _fastStartStepDuration: number = 0.2;
    private _startEase: string = `back.in(4)`;
    private _defaultSpinStepDuration: number = 0.04;
    private _normalStopStepDuration: number = 0.5;
    private _fastStopStepDuration: number = 0.4;
    private _normalStopEase: string = `elastic.out(1.5, 0.95)`;
    private _fastStopEase: string = `elastic.out(1.2, 0.75)`;

    private _removedSymbolPool: WalkOfShameSymbol[] = [];
    private _maxRemovedSymbolPoolLength: number = 3;

    constructor(reelId: number) {
        this.events = WalkOfShame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data: IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this.reelId = reelId;
        this.reelState = ReelState.IDLE;
        this._symsNumTotal = ArrayHelper.arraySum(this._symbolsNumInData);
        this.reelView = new WalkOfShameReelView(reelId);
        this.initSymbols();
    }

    private _reelState: ReelState;

    get reelState(): ReelState {
        return this._reelState;
    }

    set reelState(value: ReelState) {
        if (value === this._reelState) {
            return;
        }

        const oldState = this._reelState;
        this._reelState = value;
        this.events.trigger("ReelStateChange", <IReelStateEventData>{
            reelId: this.reelId,
            newState: this._reelState,
            oldState: oldState
        });
    }

    // Get a position.
    private static randomizeReelSetPosition(reelSet: string[]): number {
        // Start on 10 to make sure there is enough number included.
        return MathHelper.randomNumberInRange(4, reelSet.length - 1);
    }

    private static getRandomSymbolNameFromReelSet(reelSet: string[]): string {
        let symbolName: string;
        do {
            symbolName = ArrayHelper.getRandomValue<string>(reelSet);
        } while (symbolName.includes("W") || WalkOfShameSymbolConfig.SYMBOLS[symbolName].isSpecial);

        return symbolName;
    }

    public createSymbol(symName: string, state: WalkOfShameSymbolState): WalkOfShameSymbol {
        return new WalkOfShameSymbol(symName, this.reelId, state);
    }

    public startSpin(active: boolean, delay: number): Promise<any> {
        this._extraStopDelay = 0;
        this._stopCounter = 0;
        this.isQuickStop = false;
        this.reelView.wantBounce = false;

        if (!active) {
            this.reelState = ReelState.INACTIVE;
            return Promise.resolve();
        } else {
            this.setNextState(
                ReelState.START,
                WalkOfShame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? this._fastStartStepDuration : this._normalStartStepDuration,
                this._startEase
            );
            const tl = gsap.timeline({
                onComplete: () => {
                    if (active) {
                        this.setNextState(ReelState.SPIN, this._defaultSpinStepDuration, 'none');
                        this.spinStep();
                    }
                }
            });
            tl.add(this.reelView.step(this.addNextNewSymbol(WalkOfShameSymbolState.SPIN), () => this.removeBottomSymbol()), delay);
            return new Promise<any>(res => {
                this.events.once(ReelState.SPIN, async () => {
                    res(undefined);
                });
            });
        }
    }

    public spinStep(): TimelineLite {
        const tl = gsap.timeline({
            onComplete: () => {
                if (this.reelState === ReelState.SPIN) {
                    this.spinStep();
                    return;
                }
                if (this.reelState === ReelState.STOPPING) {
                    if (this._stopCounter > 1) {
                        this._stopCounter--;
                        this.spinStep();
                        return;
                    }

                    this._stopCounter = 0;
                    this.stopStep();
                }
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(WalkOfShameSymbolState.SPIN), () => this.removeBottomSymbol()));
        return tl;
    }

    public stopStep(): TimelineLite {
        this.setNextState(
            ReelState.STOPPING,
            (WalkOfShame.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopStepDuration : this._normalStopStepDuration,
            (WalkOfShame.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopEase : this._normalStopEase
        );
        const tl = gsap.timeline({
            onStart: () => {
                this.reelView.wantBounce = true;
            },
            onComplete: () => {
                this.reelState = ReelState.STOPPED;
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(WalkOfShameSymbolState.NORMAL), () => this.removeBottomSymbol()));
        this.reelView.allSymbols.forEach((symbol) => {
            tl.add(() => {
                if (!symbol.transform) {
                    return;
                }
                symbol.getStateAnimation(WalkOfShameSymbolState.NORMAL);
            }, 0);
        });
        return tl;
    }

    public hasNearWin(): boolean {
        return WalkOfShame.model.parsedGameData && (WalkOfShame.model.parsedGameData.nearWinBigWinReels[this.reelId] || WalkOfShame.model.parsedGameData.nearWinReels[this.reelId]);
    }

    public addStopDelayIfNotEnough(value: number): void {
        // Logger.logDev(`reel ${this.reelId} go added delay ${value}, totalDelayBefore ${this._extraStopDelay}`);
        this._extraStopDelay = Math.max(value, this._extraStopDelay);
        // Logger.logDev(`totalDelayAfter ${this._extraStopDelay}`);
    }

    // This is just estimated time
    public getDefaultStoppingTime(): number {
        return (this._symsNumTotal - 1) * this._defaultSpinStepDuration + this._normalStopStepDuration;
    }

    public tryStop(spinStopOffset: number, delay: number, quickStop: boolean): void {
        this.isQuickStop = quickStop;

        if (this.reelState === ReelState.INACTIVE) {
            this.reelState = ReelState.STOPPED;
            return;
        }

        if (this.reelState === ReelState.STOPPING) {
            // Logger.logDev(`reel ${this.reelId} is already stopping.`);
            return;
        }

        if (this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
            this._reelStopDelayTimer.pause();
            this._reelStopDelayTimer.kill();
        }

        if (this.isQuickStop) {
            this._extraStopDelay = 0;
        }

        const totalDelay: number = delay + this._extraStopDelay;
        this._extraStopDelay = 0;
        // Logger.logDev(`reel ${this.reelId} try to stop in extra delay ${this._extraStopDelay}, total delay ${totalDelay}`);
        if (totalDelay > 0) {
            this._reelStopDelayTimer = gsap.to(this, totalDelay, {onComplete: () => this.tryStop(spinStopOffset, 0, quickStop)});
            return;
        }
        this.getReadyToStop(spinStopOffset);
    }

    public isDataSymbolInValidArea(index: number): boolean {
        return index >= this._symbolsNumInData[1] && index < (this._symbolsNumInData[1] + this._symbolsNumInData[2]);
    }

    public getSymbol(index: number): WalkOfShameSymbol {
        return this.reelView.allSymbols[index];
    }

    private initSymbols(): void {
        this._currentReelSet = this.makeUpStopReelSet();
        if (this._stopReelSetFilledBottomNum < this._symbolsNumInData[4]) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomBottomFillUp(this._symbolsNumInData[4] - this._stopReelSetFilledBottomNum));
        }

        for (let i: number = 0; i < this._symsNumTotal; i++) {
            this.addNextNewSymbol(WalkOfShameSymbolState.NORMAL);
        }
        this.reelView.initSymbolsPosition(this._symbolsNumInData[0] + this._symbolsNumInData[1]);
    }

    private getReelSet(name: string): string[] {
        if (WalkOfShameGameConfig.REEL_SETS[name]) {
            const reelSet: string[] = WalkOfShameGameConfig.REEL_SETS[name][this.reelId];
            if (reelSet.length < 5) {
                Logger.logDev(`ReelController.getReelSet() name = ${name}, reelId = ${this.reelId}, the reelSet is too short that might break ReelController.randomizeReelSetPosition().`)
            }
            return reelSet;
        }

        debugger;
        throw new Error(`ReelController.getReelSet(): ReelSet ${name} is missing from GameConfig!`);
    }

    // only for spin here
    private makeUpStopReelSet(): string[] {
        const stopReelSet = WalkOfShame.model.reels[this.reelId];
        this._currentFullReelSet = this.getReelSet(WalkOfShameGameConfig.getReelSetName(WalkOfShame.model.data.nextMode));
        const randomPosition: number = WalkOfShameReelController.randomizeReelSetPosition(this._currentFullReelSet);
        return this._currentFullReelSet.slice(0, randomPosition + 1).concat(stopReelSet);
    }

    private makeUpStopReelSetRandomBottomFillUp(length: number): string[] {
        return ArrayHelper.initArrayWithValues(length, () => WalkOfShameReelController.getRandomSymbolNameFromReelSet(this._currentFullReelSet));
    }

    private addNextNewSymbol(state: WalkOfShameSymbolState): WalkOfShameSymbol {
        if (this._currentReelSet.length == 0) {
            if (this._currentFullReelSet == null) {
                debugger;
                throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
            }
            this._currentReelSet = this._currentFullReelSet.concat();
        }
        const symName: string = this._currentReelSet.pop()!;
        if (symName == null || symName.length === 0) {
            debugger;
            throw new Error(`ReelController.addNextNewSymbol(): symbol name cannot be empty!`);
        }
        let symbol: WalkOfShameSymbol = this.createSymbol(symName, state);
        this.reelView.addSymbol(symbol);
        this.reelView.allSymbols.unshift(symbol);

        const previousSymbol = this.reelView.allSymbols[1];
        if (previousSymbol) {
            symbol.y = previousSymbol.y - previousSymbol.symbolHeight * 0.5 - symbol.symbolHeight * 0.5;
        }

        return symbol;
    }

    private removeBottomSymbol(): void {
        const removeSymbol = this.reelView.allSymbols.pop()!;
        this.reelView.removeSymbol(removeSymbol);
        this._removedSymbolPool.unshift(removeSymbol);
        if (this._removedSymbolPool.length > this._maxRemovedSymbolPoolLength) {
            this._removedSymbolPool.pop()!.onRemove();
        }
    }

    private setNextState(reelState: ReelState, duration?: number, ease?: string): void {
        this.reelView.nextSpinStepConfig = {
            reelState: reelState,
            duration: duration,
            ease: ease
        }
    }

    private getReadyToStop(spinStopOffset: number): void {
        let addDelayToNextReel = 0;
        this._currentReelSet = this.makeUpStopReelSet();
        const alreadyFilled = this._stopReelSetFilledBottomNum;
        const needToFill = this._symbolsNumInData[4] + spinStopOffset;

        if (alreadyFilled < needToFill) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomBottomFillUp(needToFill - alreadyFilled));
        } else {
            spinStopOffset = alreadyFilled - this._symbolsNumInData[4];
        }
        this._stopCounter = this._symsNumTotal + spinStopOffset;

        addDelayToNextReel += spinStopOffset * this._defaultSpinStepDuration;
        addDelayToNextReel += this.reelView.currentStepLeftTime;

        if (addDelayToNextReel > 0) {
            for (let i = this.reelId + 1; i < WalkOfShame.reels.length; i++) {
                WalkOfShame.reels[i]?.addStopDelayIfNotEnough(addDelayToNextReel);
            }
        }

        this.reelState = ReelState.STOPPING;
    }
}